/**
 * Note Display Modes
 * Configurable label formatting for hex keyboard keys.
 *
 * General modes (always available):
 * - 'note_number': MIDI note number + name (default, e.g. "60 C4")
 * - 'note_name': Note name only, larger text (e.g. "C4")
 * - 'coords_only': Only hex coordinates shown, no note labels
 * - 'colors_only': No text labels, just LED colors
 *
 * Microtonal modes (when tuning enabled):
 * - 'note_cents': Show nearest 12-TET note name + cent deviation (e.g. "C4 +33c")
 * - 'edo_step': Show scale degree and division count (e.g. "5\19 +632c")
 * - 'frequency': Show frequency in Hz (e.g. "440.0 Hz")
 */

import type { MicrotonalInfo } from '@/components/hex/HexKey';
import { getNoteName } from '@/lib/noteMapping';

export type NoteDisplayMode =
  | 'note_number'
  | 'note_name'
  | 'coords_only'
  | 'colors_only'
  | 'note_cents'
  | 'edo_step'
  | 'frequency';

/**
 * Format as nearest 12-TET note name + cent deviation.
 * @param midiNote - Nearest MIDI note number (0-127)
 * @param centDeviation - Deviation from nearest 12-TET note in cents
 */
export function formatNoteCents(midiNote: number, centDeviation: number): MicrotonalInfo {
  const noteName = getNoteName(midiNote);
  const rounded = Math.round(centDeviation);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '' : '';
  return {
    stepLabel: noteName,
    centsLabel: `${sign}${rounded}c`,
  };
}

/**
 * Format as EDO step degree\N with cent offset from origin.
 * @param degree - Scale degree (0-based)
 * @param divisions - Notes per period (N)
 * @param centOffset - Cent offset from origin
 */
export function formatEdoStep(degree: number, divisions: number, centOffset: number): MicrotonalInfo {
  const rounded = Math.round(centOffset);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '' : '';
  return {
    stepLabel: `${degree}\\${divisions}`,
    centsLabel: `${sign}${rounded}c`,
  };
}

/**
 * Format as frequency in Hz, with optional ratio/label.
 * @param quantizedOffsetX10 - Quantized pitch offset from origin in 0.1-cent units
 * @param baseFrequency - Reference frequency at origin key (Hz)
 * @param pitchLabel - Optional ratio/cents label from .scl (e.g. "9/8")
 */
export function formatFrequency(quantizedOffsetX10: number, baseFrequency: number, pitchLabel?: string): MicrotonalInfo {
  const freq = baseFrequency * Math.pow(2, quantizedOffsetX10 / 12000);
  const freqStr = freq >= 1000 ? freq.toFixed(0) : freq.toFixed(1);
  return {
    stepLabel: pitchLabel ?? freqStr,
    centsLabel: pitchLabel ? `${freqStr} Hz` : 'Hz',
  };
}

/**
 * Format as note name only (larger text, no number).
 */
export function formatNoteName(noteName: string): MicrotonalInfo {
  return {
    stepLabel: noteName,
    centsLabel: '',
  };
}

/**
 * Format for coords-only mode: empty note labels.
 */
export function formatCoordsOnly(): MicrotonalInfo {
  return {
    stepLabel: '',
    centsLabel: '',
  };
}

/**
 * Format for colors-only mode: empty labels.
 */
export function formatColorsOnly(): MicrotonalInfo {
  return {
    stepLabel: '',
    centsLabel: '',
  };
}
